import { CONTENT_HUB_IMAGE_FALLBACK_URL } from "@/content-hub/utils";

/**
 * Matches the YouTube poster pattern used elsewhere in the hub (e.g. default page JSON).
 * Used when the block has `url` but no persisted `thumbnail` — same situation as
 * {@link EmbedVideoCard} using ReactPlayer without a static `thumbnail` prop.
 */
export function inferYoutubePosterFromEmbedUrl(
  embedUrl: string
): string | undefined {
  const trimmed = embedUrl.trim();
  if (!trimmed) return undefined;

  const youtuBe = trimmed.match(/^https?:\/\/youtu\.be\/([^/?#]+)/i);
  if (youtuBe?.[1]) {
    return `https://i.ytimg.com/vi/${youtuBe[1]}/hqdefault.jpg`;
  }

  const shorts = trimmed.match(/youtube\.com\/shorts\/([^/?#]+)/i);
  if (shorts?.[1]) {
    return `https://i.ytimg.com/vi/${shorts[1]}/hqdefault.jpg`;
  }

  const embedId = trimmed.match(/youtube\.com\/embed\/([^/?#]+)/i);
  if (embedId?.[1]) {
    return `https://i.ytimg.com/vi/${embedId[1]}/hqdefault.jpg`;
  }

  const watchV = trimmed.match(/[?&]v=([^&#[?]+)/);
  if (/youtube\.com/i.test(trimmed) && watchV?.[1]) {
    return `https://i.ytimg.com/vi/${watchV[1]}/hqdefault.jpg`;
  }

  return undefined;
}

export type ResolveVideoBlockPosterForDetailsArgs = {
  /** Persisted block `thumbnail` (e.g. oEmbed). */
  blockThumbnail?: string;
  /** Embed `url` — used to infer YouTube poster when `blockThumbnail` is missing. */
  embedUrl?: string;
  /** Recording API thumbnail when the block is (or was) library-backed. */
  recordingFallbackThumbnail?: string;
};

/**
 * Resolves a static poster URL for the Video details `ImageUpload` when the canvas may
 * be showing ReactPlayer (no `thumbnail` in serialized props) or oEmbed thumbnail exists.
 */
export function resolveVideoBlockPosterForDetails(
  args: ResolveVideoBlockPosterForDetailsArgs
): string {
  const block = args.blockThumbnail?.trim();
  if (block) return block;

  const inferred = args.embedUrl?.trim()
    ? inferYoutubePosterFromEmbedUrl(args.embedUrl.trim())
    : undefined;
  if (inferred) return inferred;

  const fromRecording = args.recordingFallbackThumbnail?.trim();
  if (fromRecording) return fromRecording;

  return CONTENT_HUB_IMAGE_FALLBACK_URL;
}
